import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { handleClientError } from '@/lib/auth/errorInterceptor';
import { ChatButton } from '@/lib/components/assistant/ChatButton';
import { SESSION_STORAGE_KEYS } from '@/lib/constants/sessionStorageKeys';
import { FeatureFlagProvider } from '@/lib/featureFlags/FeatureFlagProvider';
import { defaultHeadersInit } from '@/lib/fetch_utils';
import { AgentAuthorityErrorsProvider } from '@/lib/layout/AgentAuthorityErrorsContext';
import { GlobalLoadingProvider } from '@/lib/layout/GlobalLoadingContext';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/lib/router/routes';

import { client } from '@/lib/heyapi/client.gen';

client.interceptors.error.use(handleClientError);

const App = () => {
  const [sequenceId, setSequenceId] = useState<string | null>(null);

  useEffect(() => {
    const sequenceData = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.SEQUENCE_ID
    );
    if (sequenceData) {
      const sequenceParsed = JSON.parse(sequenceData);
      setSequenceId(sequenceParsed.sequenceId);
    }
  }, []);

  // Configures the Hey API client to talk to our backend
  client.setConfig({
    baseUrl: import.meta.env.VITE_BACKEND_URL,
    credentials: 'include',
    headers: !sequenceId
      ? defaultHeadersInit()
      : {
          ...defaultHeadersInit(),
          'x-using-sequence-id': sequenceId,
        },
  });

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <FeatureFlagProvider>
          <GlobalLoadingProvider>
            <AgentAuthorityErrorsProvider>
              <RouterProvider router={router} />
              <ReactQueryDevtools initialIsOpen={false} />
            </AgentAuthorityErrorsProvider>
          </GlobalLoadingProvider>
          <ChatButton />
        </FeatureFlagProvider>
      </QueryClientProvider>
    </>
  );
};

export default App;
